import type { FormatId } from '../types/api';

interface PlatformChipDef {
  id: FormatId;
  label: string;
  size: string;
  enabled: boolean;
}

const CHIPS: PlatformChipDef[] = [
  { id: 'ig_square', label: 'Instagram Square', size: '1080 × 1080', enabled: true },
  { id: 'ig_portrait', label: 'Instagram Portrait', size: '1080 × 1350', enabled: true },
  { id: 'facebook', label: 'Facebook', size: '1200 × 630', enabled: false },
  { id: 'threads', label: 'Threads', size: '1080 × 1350', enabled: false },
  { id: 'linkedin', label: 'LinkedIn', size: '1200 × 627', enabled: false },
  { id: 'x', label: 'X / Twitter', size: '1600 × 900', enabled: false },
];

interface Props {
  value: FormatId[];
  onChange: (value: FormatId[]) => void;
}

/**
 * Multi-select chip picker for the target platform(s).
 *
 * Per scoping (master spec §1, "Decisions"): Instagram Square + Portrait are
 * enabled in v1; the other 4 are rendered disabled with a "Coming soon"
 * tooltip so the marketer sees the roadmap. Phase 6 enables Facebook /
 * Threads, future PRs do LinkedIn + X.
 */
export function PlatformChips({ value, onChange }: Props) {
  function toggle(id: FormatId, enabled: boolean) {
    if (!enabled) return;
    onChange(
      value.includes(id) ? value.filter((v) => v !== id) : [...value, id],
    );
  }

  return (
    <div className="platforms">
      {CHIPS.map((chip) => {
        const active = value.includes(chip.id);
        const className = [
          'chip',
          active ? 'is-active' : '',
          chip.enabled ? '' : 'is-disabled',
        ]
          .filter(Boolean)
          .join(' ');
        return (
          <button
            key={chip.id}
            type="button"
            className={className}
            onClick={() => toggle(chip.id, chip.enabled)}
            title={chip.enabled ? '' : 'Coming soon'}
            aria-pressed={active}
            aria-disabled={!chip.enabled}
          >
            <span className="chip-label">{chip.label}</span>
            <span className="chip-meta">
              {chip.size}
              {chip.enabled ? '' : ' · Coming soon'}
            </span>
          </button>
        );
      })}
    </div>
  );
}
